/**
 * Grouping of solver constraints into "tables".
 *
 * Mirrors {@link ./solutionVariables}, but for constraints. Each constraint in a
 * {@link Model} may carry a `group` (the table it belongs to, e.g. "dcpf") and
 * `indices` (the values of that table's index columns, e.g.
 * `{ edge_id: "edge3", timestamp: 0 }`). This module joins those constraints
 * with a HiGHS solution to produce, per group, a table whose rows are the index
 * values augmented with the constraint's definition and its dual (shadow price).
 */

import { Constraint, HighsRawSolution, IndexValue } from './highs';

/** One constraint within a group: its index values plus definition and dual. */
export interface SolutionConstraintRow {
  /** Underlying constraint name (used as a stable React key). */
  name: string;
  /** Index column values, keyed by column name. */
  indices: Record<string, IndexValue>;
  /** Human-readable definition, e.g. "P_line_edge3_t0 - 10 theta_bus5_t0 = 0". */
  definition: string;
  /** Dual value (shadow price) from the solution, if available. */
  dual?: number;
}

/** A group of constraints sharing the same index columns, e.g. "dcpf". */
export interface SolutionConstraintTable {
  /** Group name, e.g. "dcpf" or "balance". */
  name: string;
  /** Ordered index column names, e.g. ["edge_id", "timestamp"]. */
  columns: string[];
  rows: SolutionConstraintRow[];
}

/** Fallback group and column for constraints that carry no grouping metadata. */
const UNGROUPED_TABLE = 'other';
const UNGROUPED_COLUMN = 'constraint';

/**
 * Join a model's constraints with a solution, grouping them into tables.
 *
 * Constraints are bucketed by their `group`; within each table the columns are
 * the ordered union of index keys seen, and rows are sorted by their index
 * values. Constraints without grouping metadata fall into an "other" table
 * keyed by name, so nothing is silently dropped.
 */
export function extractConstraintTables(
  constraints: Constraint[],
  solution?: HighsRawSolution
): SolutionConstraintTable[] {
  const dualOf = makeDualLookup(constraints, solution);

  // Preserve first-seen order of both groups and their columns.
  const tables = new Map<string, SolutionConstraintTable>();

  constraints.forEach((constraint) => {
    const groupName = constraint.group ?? UNGROUPED_TABLE;
    const indices: Record<string, IndexValue> =
      constraint.group !== undefined && constraint.indices
        ? constraint.indices
        : { [UNGROUPED_COLUMN]: constraint.name };

    let table = tables.get(groupName);
    if (!table) {
      table = { name: groupName, columns: [], rows: [] };
      tables.set(groupName, table);
    }

    for (const column of Object.keys(indices)) {
      if (!table.columns.includes(column)) {
        table.columns.push(column);
      }
    }

    table.rows.push({
      name: constraint.name,
      indices,
      definition: `${formatLinearExpression(constraint.coefficients)} ${constraint.sense} ${formatNumber(constraint.rhs)}`,
      dual: dualOf(constraint.name),
    });
  });

  const result = Array.from(tables.values());
  for (const table of result) {
    table.rows.sort((a, b) => compareByColumns(a.indices, b.indices, table.columns));
  }
  return result;
}

/**
 * Build a lookup from constraint name to its dual value. HiGHS returns `Rows`
 * either as an object keyed by constraint name, or as an array whose elements
 * each carry their own `Name` (and which HiGHS may reorder relative to the input
 * model). Key by name in both cases, only falling back to positional alignment
 * for array rows that lack a `Name`.
 */
function makeDualLookup(
  constraints: Constraint[],
  solution?: HighsRawSolution
): (name: string) => number | undefined {
  const rows = solution?.Rows;
  if (!rows) {
    return () => undefined;
  }
  const byName = new Map<string, number | undefined>();
  if (Array.isArray(rows)) {
    (rows as Array<{ Name?: string; Dual?: number }>).forEach((row, i) => {
      const key = row?.Name ?? constraints[i]?.name;
      if (key !== undefined) {
        byName.set(key, row?.Dual);
      }
    });
  } else {
    Object.entries(rows).forEach(([name, row]) => {
      byName.set(name, row?.Dual);
    });
  }
  return (name) => byName.get(name);
}

/** Format a linear combination of variables in LP style, e.g. "a - 10 b + c". */
export function formatLinearExpression(coefficients: Map<string, number>): string {
  const terms: string[] = [];
  coefficients.forEach((coeff, varName) => {
    if (coeff === 0) return;
    if (coeff === 1) {
      terms.push(varName);
    } else if (coeff === -1) {
      terms.push(`-${varName}`);
    } else {
      terms.push(`${formatNumber(coeff)} ${varName}`);
    }
  });
  return terms.join(' + ').replace(/\+ -/g, '- ');
}

/** Show integers plainly and round non-integers to 3 decimals. */
function formatNumber(n: number): string {
  return Number.isInteger(n) ? String(n) : Number(n.toFixed(3)).toString();
}

/** Order rows by their index columns: numbers numerically, everything else lexically. */
function compareByColumns(
  a: Record<string, IndexValue>,
  b: Record<string, IndexValue>,
  columns: string[]
): number {
  for (const column of columns) {
    const av = a[column];
    const bv = b[column];
    if (av === bv) continue;
    if (typeof av === 'number' && typeof bv === 'number') {
      return av - bv;
    }
    return String(av ?? '').localeCompare(String(bv ?? ''));
  }
  return 0;
}
